import clsx from 'clsx';
import {useCallback, useEffect, useReducer, useRef} from 'preact/hooks';
import React, {ReactType} from 'react';
import {Message, MessageType} from '../message';
import defaultTheme from './message-group.module.pcss';

type State = typeof initialState;
type Action = {
  type: 'add' | 'remove';
  payload: MessageProps;
};

type Props = {
  /**
   * Element node
   * @default 'div'
   */
  as?: ReactType;
  /**
   * Duration before auto hide
   * @default 1000
   */
  autoHideDuration?: number;
  className?: string;
  theme?: {
    root?: string;
    list?: string;
    visible?: string;
  };
};

export interface MessageProps {
  text: string;
  type: MessageType;
  id: number;
  close?: boolean;
}

const initialState = Object.freeze({
  messages: [] as MessageProps[],
});

function reducer(state: State, action: Action) {
  const payload = action.payload;
  const {text = ''} = payload;

  switch (action.type) {
    case 'add':
      return {
        ...state,
        messages: [...state.messages, payload],
      };
    case 'remove':
      if (!text) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        messages: state.messages.filter(item => item.id !== payload.id),
      };
    default:
      throw new Error();
  }
}

export const MessageGroup = ({
  as: ComponentProp = 'div',
  autoHideDuration = 100000,
  className,
  theme = defaultTheme,
  ...rest
}: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const timer = useRef(0);

  useEffect(() => {
    window.addEventListener('message:updated', handleMessage as EventListener);

    return () => {
      window.removeEventListener(
        'message:updated',
        handleMessage as EventListener,
      );
    };
  }, []);

  const setHideTimer = useCallback(
    (duration: number, msg: any) => {
      window.clearTimeout(timer.current);

      timer.current = window.setTimeout(() => {
        dispatch({
          payload: msg,
          type: 'remove',
        });
      }, duration);
    },
    [autoHideDuration],
  );

  const handleMessage = (event: CustomEvent) => {
    const detail = event.detail;
    const payload = {
      ...detail,
      id: Date.now(),
    };

    dispatch({
      payload,
      type: 'add',
    });

    setHideTimer(autoHideDuration, payload);
  };

  const isVisible = state.messages.length > 0;
  const classes = clsx(theme.root, className, {
    [theme.visible as any]: isVisible,
  });

  return (
    <ComponentProp className={classes} {...rest}>
      {isVisible && (
        <ul className={theme.list}>
          {state.messages.map((item, index) => (
            <li key={index}>
              <Message
                type={item.type}
                text={item.text}
                handleClick={() => {
                  dispatch({
                    payload: {
                      id: item.id,
                      text: item.text,
                      type: item.type,
                    },
                    type: 'remove',
                  });
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </ComponentProp>
  );
};
